import { useState } from 'react'

import { FireIcon } from '@hugeicons/react'

import {
  OBJECTIVES_FEATURE_COPY,
  OBJECTIVES_HERO_ART
} from '@/shared/resourceModuleParity'
import { GameIcon } from '@/shared/ui/GameIcon'
import { ListSection, PageHero, Screen } from '@/shared/ui/Layout'
import { PageMeta } from '@/shared/lib/meta/page-meta'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { ObjectiveDetailSheet } from './components/ObjectiveDetailSheet'
import { ObjectiveItem } from './components/ObjectiveItem'
import { MilestoneObjectiveCard } from './components/MilestoneObjectiveCard'
import { useObjectivesListView } from './hooks/useObjectivesListView'
import type { Goal } from './model/milestones'

export const ObjetivosPage = () => {
  const {
    groups,
    completedCount,
    totalCount,
    overallPercent,
    totalPossibleCoins,
    earnedCoins,
    isLoading,
    isEmpty
  } = useObjectivesListView()

  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null)

  return (
    <>
      <PageMeta
        title={OBJECTIVES_FEATURE_COPY.plural}
        description={OBJECTIVES_FEATURE_COPY.pageDescription()}
      />
      <Screen>
        <Screen.Context fullBleed>
          <PageHero
            variant="full"
            title={OBJECTIVES_FEATURE_COPY.plural}
            art={OBJECTIVES_HERO_ART}
            action={
              totalPossibleCoins > 0 ? (
                <div className="flex items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1.5">
                  <GameIcon name="coins" size={13} />
                  <span className="text-2xs font-bold leading-none text-white">
                    {earnedCoins}/{totalPossibleCoins}
                  </span>
                </div>
              ) : undefined
            }
          >
            <div className="flex items-end justify-between gap-3">
              <div className="flex items-baseline gap-1 leading-none">
                <p className="m-0 text-4xl font-black leading-none text-white">
                  {overallPercent}
                </p>
                <span className="text-xl font-bold text-white/80">%</span>
                <span className="ml-1 text-sm text-white/80">concluído</span>
              </div>

              <span className="shrink-0 text-2xs font-bold leading-none text-white">
                {completedCount}/{totalCount}
              </span>
            </div>
            <div className="mt-2.5">
              <ProgressBar
                value={overallPercent}
                size="sm"
                className="[&>div]:h-2 [&>div]:bg-white/20 [&>div]:shadow-none [&>div>div]:bg-white/90"
                aria-label={OBJECTIVES_FEATURE_COPY.progressAriaLabel()}
              />
            </div>
          </PageHero>
        </Screen.Context>

        <Screen.Content className="pt-4">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 animate-pulse rounded-[1rem] bg-gray-100" />
              ))}
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <p className="m-0 text-base font-bold text-gray-900">
                Nenhuma meta disponível
              </p>
              <p className="m-0 text-sm text-gray-700">
Novas metas aparecerão aqui quando disponíveis.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-5 pb-8 pt-1">
              {groups.map(group => (
                <ListSection
                  key={group.status}
                  id={`objectives-section-${group.status}`}
                  title={group.label}
                  count={group.items.length}
                  status={group.status}
                  icon={
                    group.status === 'progress' ? (
                      <FireIcon size={14} variant="stroke" aria-hidden />
                    ) : undefined
                  }
                >
                  {group.items.map(goal =>
                    goal.kind === 'ladder' ? (
                      <MilestoneObjectiveCard
                        key={goal.id}
                        goal={goal}
                        onPress={() => setSelectedGoal(goal)}
                      />
                    ) : (
                      <ObjectiveItem
                        key={goal.id}
                        objective={goal.objective}
                        onPress={() => setSelectedGoal(goal)}
                      />
                    )
                  )}
                </ListSection>
              ))}
            </div>
          )}
        </Screen.Content>
      </Screen>

      <ObjectiveDetailSheet
        goal={selectedGoal}
        onClose={() => setSelectedGoal(null)}
      />
    </>
  )
}
